import { DisclosureChevron, ExpandIn } from "@/components/common/Disclosure";
import { isTerminalSubagentStatus, subagentProfileLabel, type ToolCallStatus } from "@/lib/chat";
import { subagentStatusLabel, type ToolUseRenderBlock } from "@/lib/chat-render";
import { useTick } from "@/lib/useTick";
import { useChatStore } from "@/stores/useChatStore";
import { useLayoutStore } from "@/stores/useLayoutStore";
import { SubagentRunResult } from "./SubagentRunResult";
import { useDisclosureOpen } from "./useDisclosureOpen";
import { HEADER_BUTTON_CLASS, OutputBox, StatusDot } from "./ToolEntry";

/** 同一ターンの run_subagent をまとめ、行クリックでタスクタブの詳細へ繋ぐカード。 */
export function SubagentGroupCard({ tools }: { tools: ToolUseRenderBlock[] }) {
	const [expandedByUser, setExpanded] = useDisclosureOpen(`subagents:${tools[0].key}`);
	const expanded = expandedByUser ?? false;
	const selectSubagentRun = useChatStore((state) => state.selectSubagentRun);
	const openBottomPaneTab = useLayoutStore((state) => state.openBottomPaneTab);
	const running = tools.filter((tool) => tool.status === "running");
	const status: ToolCallStatus =
		running.length > 0
			? "running"
			: tools.some((tool) => tool.status === "waiting_user")
				? "waiting_user"
				: tools.some((tool) => tool.status === "error")
					? "error"
					: tools.some((tool) => tool.status === "cancelled")
						? "cancelled"
						: tools.some((tool) => tool.status === "skipped")
							? "skipped"
							: "completed";
	// 注目すべき run が 1 つに絞れるなら、畳んだままでも何の実行か分かるよう説明と状態を見出しに出す。
	const highlighted = running.length === 1 ? running[0] : tools.length === 1 ? tools[0] : null;
	// run 一覧ではなく tool_result 由来でも解決できる id で遷移する。一覧が届く前・取得に失敗して
	// いる間も実行履歴へ入れるようにするため (タスクタブは id だけで詳細を開ける)。
	const select = (tool: ToolUseRenderBlock) => {
		if (tool.subagentRunId === undefined) return;
		selectSubagentRun(tool.subagentRunId);
		openBottomPaneTab("tasks");
	};
	return (
		<div data-testid="subagent-group-card" data-status={status} className="min-w-0">
			<button type="button" onClick={() => setExpanded(!expanded)} className={HEADER_BUTTON_CLASS}>
				<StatusDot status={status} />
				{highlighted ? (
					<SubagentLabel tool={highlighted} />
				) : (
					<span className="min-w-0 truncate">
						{running.length > 0
							? `${running.length}個のサブエージェントを実行中`
							: `${tools.length}個のサブエージェント`}
					</span>
				)}
				<DisclosureChevron open={expanded} size={12} />
			</button>
			{expanded && (
				<ExpandIn className="mt-1">
					{tools.map((tool) => (
						<SubagentGroupRow key={tool.key} tool={tool} onSelect={() => select(tool)} />
					))}
				</ExpandIn>
			)}
		</div>
	);
}

/**
 * 展開時の 1 行。見出しと同じく状態・ターン数・経過時間を添え、終端した run は結果 (要約・失敗理由)
 * もその場で読めるようにする (タスクタブまで辿らないと何を報告したのか分からない状態を避ける)。
 */
export function SubagentGroupRow({
	tool,
	onSelect,
}: {
	tool: ToolUseRenderBlock;
	onSelect: () => void;
}) {
	const run = tool.subagentRun;
	return (
		<div className="min-w-0">
			<button
				type="button"
				data-testid="subagent-group-row"
				// run id すら無い間 (foreground の起動直後・起動拒否) は詳細を開けない。
				disabled={tool.subagentRunId === undefined}
				onClick={onSelect}
				className="flex w-full items-center gap-1.5 border-0 bg-transparent px-0 py-0.5 text-left text-caption text-fg-2 cursor-pointer disabled:cursor-default"
			>
				<StatusDot status={tool.status} />
				<SubagentLabel tool={tool} />
				{run && (
					<>
						<span className="shrink-0 text-fg-3">{subagentProfileLabel(run.profile)}</span>
						{run.isBackground && <span className="shrink-0 text-fg-3">バックグラウンド</span>}
					</>
				)}
			</button>
			{run && isTerminalSubagentStatus(run.status) && (
				<div className="mb-1 pl-3">
					<SubagentRunResult run={run} />
				</div>
			)}
			{/* run 行が無いまま終端したツール (起動前の停止・一覧取得の失敗) は、結果が tool_result に
			    しか無い。ここで出さないとどこからも読めなくなる。 */}
			{run === undefined && tool.status !== "running" && tool.output && (
				<div className="mb-1 pl-3">
					<OutputBox output={tool.output} />
				</div>
			)}
		</div>
	);
}

/** run が読めていればそちらの説明を、まだなら起動時の入力を使う。 */
function subagentDescription(tool: ToolUseRenderBlock): string {
	if (tool.subagentRun) return tool.subagentRun.description;
	return typeof tool.input.description === "string" ? tool.input.description : "サブエージェント";
}

function labelText(tool: ToolUseRenderBlock, now: number): string {
	const description = subagentDescription(tool);
	const status = subagentStatusLabel(tool, now);
	return status ? `${description} · ${status}` : description;
}

/** 終端した run は経過が固定されるので、実行中のときだけ毎秒の tick を購読する。 */
function SubagentLabel({ tool }: { tool: ToolUseRenderBlock }) {
	if (tool.status === "running") return <TickingLabel tool={tool} />;
	// 経過は endedAt で確定しているため now は参照されない。
	return <LabelText text={labelText(tool, 0)} />;
}

function TickingLabel({ tool }: { tool: ToolUseRenderBlock }) {
	const now = useTick(1_000);
	return <LabelText text={labelText(tool, now)} />;
}

/** 経過が毎秒変わるので、title ではなく data-tooltip で全文を出す。 */
function LabelText({ text }: { text: string }) {
	return (
		<span data-tooltip={text} data-tooltip-if-truncated className="min-w-0 truncate">
			{text}
		</span>
	);
}
